import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { commonTexts } from 'i18n';
import ConfirmationDialog from 'components/ConfirmationDialog';

type ConfirmFn = (message: string) => Promise<boolean>;

const NavigationPromptContext = createContext<ConfirmFn>(() =>
  Promise.resolve(true),
);

export function NavigationPromptProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((nextMessage) => {
    setMessage(nextMessage);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setMessage(null);
  }, []);

  return (
    <NavigationPromptContext.Provider value={confirm}>
      {children}
      {message !== null && (
        <ConfirmationDialog
          title={<FormattedMessage {...commonTexts.confirm} />}
          content={message}
          submitText={<FormattedMessage {...commonTexts.confirm} />}
          onSubmit={() => close(true)}
          onClose={() => close(false)}
        />
      )}
    </NavigationPromptContext.Provider>
  );
}

export const useNavigationPrompt = () => useContext(NavigationPromptContext);
